import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Link2, 
  Unlink, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Building2,
  FileText,
  Loader2
} from 'lucide-react';

export default function XeroSettings() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchStatus();
    
    // Handle OAuth callback messages
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'true' || error) {
      if (success === 'true') toast.success('Xero connected successfully!');
      else toast.error(`Xero connection failed: ${error}`);
      // Clean the OAuth params off the current URL (works embedded in /settings)
      const url = new URL(window.location.href);
      url.searchParams.delete('success');
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''));
    }
  }, [searchParams]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/xero/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Xero status:', error);
      toast.error('Failed to check Xero connection status');
    } finally {
      setLoading(false);
    }
  };

  const connectXero = async () => {
    try {
      setConnecting(true);
      const response = await api.get('/xero/auth/login');
      
      if (response.data.authorization_url) {
        // Redirect to Xero OAuth
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error('Failed to initiate Xero connection:', error);
      toast.error('Failed to start Xero connection');
      setConnecting(false);
    }
  };

  const disconnectXero = async () => {
    if (!window.confirm('Are you sure you want to disconnect Xero?')) return;
    
    try {
      await api.delete('/xero/disconnect');
      toast.success('Xero disconnected');
      setStatus({ connected: false });
    } catch (error) {
      console.error('Failed to disconnect Xero:', error);
      toast.error('Failed to disconnect Xero');
    }
  };

  const refreshToken = async () => {
    try {
      await api.post('/xero/refresh');
      toast.success('Token refreshed successfully');
      fetchStatus();
    } catch (error) {
      console.error('Failed to refresh token:', error);
      toast.error('Failed to refresh token');
    }
  };

  const testConnection = async () => {
    try {
      setTestResult({ loading: true });
      const response = await api.get('/xero/invoices?page=1');
      setTestResult({
        success: true,
        message: `Found ${response.data.count} invoices`,
        data: response.data
      });
      toast.success('Xero connection working!');
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || error.message
      });
      toast.error('Connection test failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Xero Integration</h1>
        <p className="text-muted-foreground">
          Connect to Xero to sync invoice data and calculate true principal amounts
        </p>
      </div>

      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Connection Status
          </CardTitle>
          <CardDescription>
            Manage your Xero accounting integration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {status?.connected ? (
                <>
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                  <div>
                    <p className="font-medium text-green-700">Connected</p>
                    <p className="text-sm text-muted-foreground">
                      Connected on {new Date(status.connected_at).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="h-6 w-6 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-600">Not Connected</p>
                    <p className="text-sm text-muted-foreground">
                      Click Connect to link your Xero account
                    </p>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex gap-2">
              {status?.connected ? (
                <>
                  <Button variant="outline" size="sm" onClick={refreshToken}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Token
                  </Button>
                  <Button variant="destructive" size="sm" onClick={disconnectXero}>
                    <Unlink className="h-4 w-4 mr-2" />
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button onClick={connectXero} disabled={connecting}>
                  {connecting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4 mr-2" />
                  )}
                  Connect to Xero
                </Button>
              )}
            </div>
          </div>

          {/* Connected Organisations */}
          {status?.connected && status.organisations && (
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium mb-2">Connected Organisations:</p>
              <div className="flex flex-wrap gap-2">
                {status.organisations.map((org, idx) => (
                  <Badge key={idx} variant="secondary">
                    <Building2 className="h-3 w-3 mr-1" />
                    {org}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Token Status */}
          {status?.connected && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Token Status:</span>
                {status.expired ? (
                  <Badge variant="destructive">Expired</Badge>
                ) : (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    Valid
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          {/* Current Redirect URI (for debugging) */}
          {status?.current_redirect_uri && (
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium mb-1">Current Callback URL:</p>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded block overflow-x-auto">
                {status.current_redirect_uri}
              </code>
              <p className="text-xs text-muted-foreground mt-1">
                This URL must be registered in your Xero App settings
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Connection Card */}
      {status?.connected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Test Connection
            </CardTitle>
            <CardDescription>
              Verify the integration is working by fetching invoices
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={testConnection} variant="outline">
              {testResult?.loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Test Invoice Fetch
            </Button>

            {testResult && !testResult.loading && (
              <Alert variant={testResult.success ? "default" : "destructive"}>
                <AlertDescription>
                  {testResult.success ? (
                    <span className="text-green-700">✓ {testResult.message}</span>
                  ) : (
                    <span>{testResult.message}</span>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Usage Info */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            <strong>1. True Principal Calculation:</strong> When viewing legal cases, 
            the system will fetch invoice data from Xero to calculate the actual principal 
            amount at risk (excluding late fees and penalties).
          </p>
          <p>
            <strong>2. Invoice Matching:</strong> Invoices are matched by SF number 
            (e.g., SF-2111) using the Reference or Invoice Number field in Xero.
          </p>
          <p>
            <strong>3. Multiple Loans:</strong> For restructured loans (SF-2111/2, SF-2111/3), 
            the system will find the most recent loan data.
          </p>
          <p>
            <strong>4. Data Sync:</strong> Data is fetched in real-time from Xero - 
            no data is permanently stored from Xero.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
